import React, { createContext, useContext, useState } from 'react'

export interface TimePeriodOption {
  label: string
  value: string
  days: number
}

// eslint-disable-next-line react-refresh/only-export-components
export const TIME_PERIOD_OPTIONS: TimePeriodOption[] = [
  { label: 'Last 7 Days', value: '7d', days: 7 },
  { label: 'Last 30 Days', value: '30d', days: 30 },
  { label: 'Last 90 Days', value: '90d', days: 90 },
]

interface TimePeriodContextType {
  selectedPeriod: string
  setSelectedPeriod: (period: string) => void
  selectedDays: number
  options: TimePeriodOption[]
}

const TimePeriodContext = createContext<TimePeriodContextType | undefined>(undefined)

const STORAGE_KEY = 'or-time-period'

export function TimePeriodProvider({ children }: { children: React.ReactNode }) {
  const [selectedPeriod, setSelectedPeriodState] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored && TIME_PERIOD_OPTIONS.some(o => o.value === stored)) {
        return stored
      }
    } catch {
      // Ignore localStorage errors
    }
    return '30d'
  })

  const setSelectedPeriod = (period: string) => {
    setSelectedPeriodState(period)
    try {
      localStorage.setItem(STORAGE_KEY, period)
    } catch {
      // Ignore
    }
  }

  const selectedDays = TIME_PERIOD_OPTIONS.find(o => o.value === selectedPeriod)?.days ?? 30

  return (
    <TimePeriodContext.Provider value={{ selectedPeriod, setSelectedPeriod, selectedDays, options: TIME_PERIOD_OPTIONS }}>
      {children}
    </TimePeriodContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTimePeriod(): TimePeriodContextType {
  const context = useContext(TimePeriodContext)
  if (context === undefined) {
    throw new Error('useTimePeriod must be used within a TimePeriodProvider')
  }
  return context
}
